import type { CurrentUser, LoginResponse, TenantAccess } from '@konyaxl/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiRequestError, api, hasSession, loadTokens, setTokens } from './api.js';

interface AuthState {
  user: CurrentUser | null;
  tenants: TenantAccess[];
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [tenants, setTenants] = useState<TenantAccess[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Oturum bilgisi her açılışta sunucudan doğrulanır. localStorage'daki
   * token'a körü körüne güvenilmez — hesap devre dışı bırakılmış olabilir.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!loadTokens()) {
        setLoading(false);
        return;
      }

      try {
        const [me, list] = await Promise.all([
          api.get<CurrentUser>('/auth/me'),
          api.get<TenantAccess[]>('/tenants'),
        ]);
        if (!cancelled) {
          setUser(me);
          setTenants(list);
        }
      } catch (err) {
        if (!cancelled && err instanceof ApiRequestError && err.status === 401) {
          setTokens(null, null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.post<LoginResponse>('/auth/login', { username, password });
    setTokens(res.accessToken, res.refreshToken);
    const list = await api.get<TenantAccess[]>('/tenants');
    setUser(res.user);
    setTenants(list);
  }, []);

  const logout = useCallback(async () => {
    const raw = localStorage.getItem('konyaxl.session');
    if (raw) {
      try {
        const { refresh } = JSON.parse(raw) as { refresh?: string };
        if (refresh) await api.post('/auth/logout', { refreshToken: refresh });
      } catch {
        // Sunucuya ulaşılamasa da yerel oturum kapatılır.
      }
    }
    setTokens(null, null);
    setUser(null);
    setTenants([]);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, tenants, loading, login, logout }),
    [user, tenants, loading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth, AuthProvider içinde kullanılmalı');
  return ctx;
}

export { hasSession };
